import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import { 
  Settings, Save, Wrench, DollarSign, Clock, Package,
  ChevronDown, ChevronRight, AlertCircle, Plus, Trash2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_BLUE = '#2b3380';

const IQCConfiguration = ({ user, onLogout }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [expandedParams, setExpandedParams] = useState({});
  const [activeTab, setActiveTab] = useState('repair_mappings');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/qc-stage/iqc/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to load IQC config:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/qc-stage/iqc/config`, {
        repair_mappings: config.repair_mappings
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasChanges(false);
      alert('Configuration saved successfully!');
    } catch (error) {
      console.error('Failed to save config:', error);
      alert('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const updateMapping = (index, field, value) => {
    const newMappings = [...config.repair_mappings];
    newMappings[index] = { ...newMappings[index], [field]: value };
    setConfig({ ...config, repair_mappings: newMappings });
    setHasChanges(true);
  };

  const addMapping = () => {
    const newMappings = [...(config.repair_mappings || []), {
      parameter_id: '',
      part_name: '',
      part_sku: '',
      severity: 'Low',
      estimated_cost: 0,
      estimated_time_hours: 0
    }];
    setConfig({ ...config, repair_mappings: newMappings });
    setHasChanges(true);
  };

  const removeMapping = (index) => {
    const newMappings = config.repair_mappings.filter((_, i) => i !== index);
    setConfig({ ...config, repair_mappings: newMappings });
    setHasChanges(true);
  };

  const toggleExpand = (paramId) => {
    setExpandedParams(prev => ({
      ...prev,
      [paramId]: !prev[paramId]
    }));
  };

  const getSeverityColor = (severity) => {
    const colors = {
      Low: 'bg-green-100 text-green-800',
      Medium: 'bg-yellow-100 text-yellow-800',
      High: 'bg-orange-100 text-orange-800',
      Critical: 'bg-red-100 text-red-800'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800';
  };

  // Get unique parameters from template
  const templateParams = config?.template?.categories?.flatMap(c => 
    c.parameters.map(p => ({ ...p, category: c.name }))
  ) || [];

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: ZOBOX_BLUE }}>
              <Settings className="inline-block mr-2 h-6 w-6" />
              IQC Configuration
            </h1>
            <p className="text-gray-600 mt-1">
              Configure repair mappings and IQC settings
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={saveConfig}
              disabled={!hasChanges || saving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-white disabled:opacity-50"
              style={{ backgroundColor: ZOBOX_BLUE }}
              data-testid="save-iqc-config-btn"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b mb-6">
          <div className="flex gap-4">
            {[
              { id: 'repair_mappings', label: 'Repair Mappings', icon: Wrench },
              { id: 'settings', label: 'IQC Settings', icon: Settings }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {hasChanges && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-yellow-600" />
            <span className="text-yellow-800">You have unsaved changes</span>
          </div>
        )}

        {/* Repair Mappings Tab */}
        {activeTab === 'repair_mappings' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Parameter → Part Mappings</h3>
                <p className="text-sm text-gray-500">
                  Define which spare part is needed when a QC parameter fails
                </p>
              </div>
              <button
                onClick={addMapping}
                className="flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-gray-50"
              >
                <Plus className="h-4 w-4" />
                Add Mapping
              </button>
            </div>

            {/* Mappings Table */}
            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">QC Parameter</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Part Required</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Part SKU</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Est. Cost</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Est. Time</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {config?.repair_mappings?.map((mapping, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <select
                          value={mapping.parameter_id || ''}
                          onChange={(e) => updateMapping(index, 'parameter_id', e.target.value)}
                          className="w-full border rounded px-2 py-1 text-sm"
                        >
                          <option value="">Select Parameter</option>
                          {templateParams.map(p => (
                            <option key={p.id} value={p.id}>
                              {p.category} → {p.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={mapping.part_name || ''}
                          onChange={(e) => updateMapping(index, 'part_name', e.target.value)}
                          className="w-full border rounded px-2 py-1 text-sm"
                          placeholder="Display Assembly"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={mapping.part_sku || ''}
                          onChange={(e) => updateMapping(index, 'part_sku', e.target.value)}
                          className="w-full border rounded px-2 py-1 text-sm font-mono"
                          placeholder="PART-XXX"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={mapping.severity || 'Low'}
                          onChange={(e) => updateMapping(index, 'severity', e.target.value)}
                          className={`border rounded px-2 py-1 text-sm ${getSeverityColor(mapping.severity)}`}
                        >
                          <option value="Low">Low</option>
                          <option value="Medium">Medium</option>
                          <option value="High">High</option>
                          <option value="Critical">Critical</option>
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          <span className="text-gray-500 mr-1">₹</span>
                          <input
                            type="number"
                            value={mapping.estimated_cost || 0}
                            onChange={(e) => updateMapping(index, 'estimated_cost', parseFloat(e.target.value) || 0)}
                            className="w-20 border rounded px-2 py-1 text-sm"
                            min="0"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          <input
                            type="number"
                            value={mapping.estimated_time_hours || 0}
                            onChange={(e) => updateMapping(index, 'estimated_time_hours', parseFloat(e.target.value) || 0)}
                            className="w-16 border rounded px-2 py-1 text-sm"
                            min="0"
                            step="0.5"
                          />
                          <span className="text-gray-500 ml-1">hrs</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => removeMapping(index)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Summary */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-medium text-blue-800 mb-2">How Repair Mappings Work</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• When a technician marks a QC parameter as "failed", the system auto-suggests the mapped part</li>
                <li>• A PNA (Parts Needs Assessment) is automatically created with all required parts</li>
                <li>• Estimated cost helps with repair budgeting and pricing decisions</li>
                <li>• Severity determines repair priority in the TRC workflow</li>
              </ul>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="bg-white rounded-lg shadow-sm border p-6 space-y-6">
            <div>
              <h3 className="font-medium mb-4">IQC Workflow Settings</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-medium">Auto-generate PNA</div>
                    <div className="text-sm text-gray-500">
                      Automatically create Parts Needs Assessment when IQC identifies failures
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config?.auto_generate_pna !== false}
                      onChange={(e) => {
                        setConfig({ ...config, auto_generate_pna: e.target.checked });
                        setHasChanges(true);
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-medium">Require Photos</div>
                    <div className="text-sm text-gray-500">
                      Mandate photos for failed parameters during IQC
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config?.require_photos === true}
                      onChange={(e) => {
                        setConfig({ ...config, require_photos: e.target.checked });
                        setHasChanges(true);
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-medium">Use Full QC Template</div>
                    <div className="text-sm text-gray-500">
                      Use all 51 parameters from QC v3.1 template for IQC
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config?.use_full_template !== false}
                      onChange={(e) => {
                        setConfig({ ...config, use_full_template: e.target.checked });
                        setHasChanges(true);
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Template Info */}
            <div className="border-t pt-6">
              <h3 className="font-medium mb-4">QC Template Info</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold" style={{ color: ZOBOX_BLUE }}>
                    v{config?.template?.version || '3.1'}
                  </div>
                  <div className="text-sm text-gray-500">Template Version</div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold" style={{ color: ZOBOX_BLUE }}>
                    {config?.template?.categories?.length || 12}
                  </div>
                  <div className="text-sm text-gray-500">Categories</div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold" style={{ color: ZOBOX_BLUE }}>
                    {templateParams.length}
                  </div>
                  <div className="text-sm text-gray-500">Parameters</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default IQCConfiguration;
